import type {Locale} from "./types";

/**
 * Plural categories from Intl.PluralRules
 */
type PluralCategory = "zero" | "one" | "two" | "few" | "many" | "other";

/**
 * Cache for PluralRules instances per locale
 */
const pluralRulesCache = new Map<string, Intl.PluralRules>();

/**
 * Get plural category for a number based on locale using native Intl.PluralRules
 * Works on both server (Node.js) and client (browser)
 *
 * @example
 * getPluralCategory(1, "en") // "one"
 * getPluralCategory(2, "en") // "other"
 * getPluralCategory(5, "es") // "other"
 * getPluralCategory(2, "es") // "other"
 */
export function getPluralCategory(count: number, locale: Locale = "en"): PluralCategory {
    let rules = pluralRulesCache.get(locale);
    if (!rules) {
        rules = new Intl.PluralRules(locale);
        pluralRulesCache.set(locale, rules);
    }
    return rules.select(count) as PluralCategory;
}

/**
 * Parse ICU plural syntax and extract categories
 * Format: {variable, plural, one {text} few {text} many {text} other {text}}
 *
 * Uses a simple state-machine parser instead of complex regex
 */
function parsePluralCategories(categoriesStr: string): Record<string, string> {
    const categories: Record<string, string> = {};
    const validCategories = ["zero", "one", "two", "few", "many", "other"];

    let i = 0;
    const len = categoriesStr.length;

    while (i < len) {
        // Skip whitespace
        while (i < len && /\s/.test(categoriesStr[i])) i++;

        // Try to match a category name (zero|one|two|few|many|other) or =N
        let categoryName = "";

        // Check for =N format (e.g., =0, =1)
        if (categoriesStr[i] === "=") {
            i++; // skip '='
            let num = "";
            while (i < len && /\d/.test(categoriesStr[i])) {
                num += categoriesStr[i++];
            }
            // Map =0 to "zero", =1 to "one", etc.
            if (num === "0") categoryName = "zero";
            else if (num === "1") categoryName = "one";
            else if (num === "2") categoryName = "two";
            else categoryName = `=${num}`; // Keep as-is for exact matches
        } else {
            // Match category name
            while (i < len && /[a-z]/i.test(categoriesStr[i])) {
                categoryName += categoriesStr[i++];
            }
        }

        if (!categoryName) break;

        // Skip whitespace before {
        while (i < len && /\s/.test(categoriesStr[i])) i++;

        // Expect {
        if (categoriesStr[i] !== "{") break;
        i++; // skip '{'

        // Find matching } (handle nested braces)
        let braceCount = 1;
        let content = "";
        while (i < len && braceCount > 0) {
            if (categoriesStr[i] === "{") braceCount++;
            else if (categoriesStr[i] === "}") braceCount--;

            if (braceCount > 0) {
                content += categoriesStr[i];
            }
            i++;
        }

        // Store the category
        if (validCategories.includes(categoryName) || categoryName.startsWith("=")) {
            categories[categoryName] = content;
        }
    }

    return categories;
}

/**
 * Apply plural syntax to template
 * Format: {variable, plural, one {singular} other {plural}}
 *
 * @example
 * applyPlural("{count, plural, one {# guest} other {# guests}}", { count: 1 }, "en")
 * // returns "1 guest"
 */
function applyPlural(template: string, params: Record<string, string | number>, locale: Locale = "en"): string {
    // Match: {variable, plural, ...rest until matching closing brace}
    // We need to find balanced braces
    let result = template;
    let startIdx = 0;

    while (true) {
        // Find start of potential plural block
        const pluralMatch = result.slice(startIdx).match(/\{(\w+),\s*plural,\s*/);
        if (!pluralMatch) break;

        const matchStart = startIdx + pluralMatch.index!;
        const variable = pluralMatch[1];
        const contentStart = matchStart + pluralMatch[0].length;

        // Find the matching closing brace
        let braceCount = 1;
        let i = contentStart;
        while (i < result.length && braceCount > 0) {
            if (result[i] === "{") braceCount++;
            else if (result[i] === "}") braceCount--;
            i++;
        }

        if (braceCount !== 0) {
            // Unbalanced braces, skip this match
            startIdx = contentStart;
            continue;
        }

        const matchEnd = i;
        const categoriesStr = result.slice(contentStart, matchEnd - 1);

        // Parse categories
        const categories = parsePluralCategories(categoriesStr);

        // Get count and category
        const count = Number(params[variable]) || 0;
        const category = getPluralCategory(count, locale);

        // Get text for exact match first, then category, then fallback to "other"
        const exactKey = `=${count}`;
        const text = categories[exactKey] ?? categories[category] ?? categories["other"] ?? "";

        // Replace # with the actual count
        const replacement = text.replace(/#/g, String(count));

        // Replace the entire plural block with the result
        result = result.slice(0, matchStart) + replacement + result.slice(matchEnd);

        // Continue searching from after the replacement
        startIdx = matchStart + replacement.length;
    }

    return result;
}

/**
 * Replace {placeholders} in template string with values
 * Supports simple interpolation and plural syntax
 *
 * @example
 * // Simple interpolation
 * interpolate("Hello {name}!", { name: "John" }) // "Hello John!"
 *
 * // Plural support
 * interpolate("{count, plural, one {# Guest} other {# Guests}}", { count: 1 }) // "1 Guest"
 * interpolate("{count, plural, one {# Guest} other {# Guests}}", { count: 5 }) // "5 Guests"
 *
 * // Spanish plurals
 * interpolate("{count, plural, one {# Huésped} other {# Huéspedes}}", { count: 2 }, "es")
 * // "2 Huéspedes"
 */
export function interpolate(template: string, params?: Record<string, string | number>, locale: Locale = "en"): string {
    if (!params) return template;

    // First apply plural transformations
    let result = applyPlural(template, params, locale);

    // Then replace remaining simple placeholders
    result = Object.entries(params).reduce((acc, [key, value]) => acc.replace(new RegExp(`\\{${key}\\}`, "g"), String(value)), result);

    return result;
}

/**
 * Get nested value from object using dot notation
 *
 * @example
 * getNestedValue({ a: { b: "hello" } }, "a.b") // "hello"
 * getNestedValue({ a: { b: "hello" } }, "a.c") // "a.c" (returns key if not found)
 */
export function getNestedValue(obj: Record<string, unknown>, key: string): string {
    const keys = key.split(".");
    let result: unknown = obj;

    for (const k of keys) {
        if (result && typeof result === "object" && k in result) {
            result = (result as Record<string, unknown>)[k];
        } else {
            return key; // Return key if path not found
        }
    }

    return typeof result === "string" ? result : key;
}
